import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Store, TrendingUp, Package, ShoppingBag, Wallet, Plus, Edit, Trash2,
  MoreHorizontal, Eye, Star, Award, ArrowUpRight, AlertCircle
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { toast } from "sonner";
import { sellerStore } from "@/lib/sellerData";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { MerchantAPI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import QRCode from "qrcode";

// Peta status pesanan (sisi merchant) -> label & warna.
const merchantStatus = {
  PENDING:   { label: "Menunggu Pembayaran", color: "bg-stone-soft/40 text-ink/70" },
  PAID:      { label: "Perlu Diterima", color: "bg-amber-warm/25 text-amber-700" },
  PACKED:    { label: "Siap Kirim",     color: "bg-sage/25 text-forest" },
  SHIPPED:   { label: "Dikirim",        color: "bg-forest/15 text-forest" },
  COMPLETED: { label: "Selesai",        color: "bg-stone-soft/50 text-ink/70" },
};

// Base URL server untuk menampilkan berkas bukti transfer (proof_url = /uploads/...).
const FILE_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/api\/?$/, "");

const statusStyle = {
  "Aktif": "bg-sage/25 text-forest",
  "Stok Rendah": "bg-amber-warm/25 text-amber-700",
  "Habis": "bg-terracotta/15 text-terracotta",
};

const orderStatusStyle = {
  "Baru": "bg-amber-warm/25 text-amber-700",
  "Diproses": "bg-sage/25 text-forest",
  "Dikirim": "bg-forest/15 text-forest",
  "Selesai": "bg-stone-soft/50 text-ink/70",
};

export default function SellerHub() {
  const { user } = useAuth();
  // Tab aktif bisa dikendalikan lewat URL, mis. /anggota/jualan?tab=pesanan
  // (dipakai notifikasi "pesanan masuk" agar langsung membuka tab Pesanan Masuk).
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["ringkasan", "produk", "pesanan"];
  const urlTab = searchParams.get("tab");
  const tab = VALID_TABS.includes(urlTab) ? urlTab : "ringkasan";
  const setTab = (t) => setSearchParams(t === "ringkasan" ? {} : { tab: t }, { replace: true });
  const payout = Number(user?.wallet?.balance_seller || 0);  // saldo penjual nyata

  // Penarikan dana
  const [wdOpen, setWdOpen] = useState(false);
  const [wdAmount, setWdAmount] = useState("");
  const [wdList, setWdList] = useState([]);
  const loadWd = () => MerchantAPI.withdrawals().then(setWdList).catch(() => {});
  const submitWithdraw = async () => {
    const amt = Number(wdAmount);
    if (!amt || amt <= 0) return toast.error("Masukkan nominal penarikan");
    if (amt > payout) return toast.error("Melebihi saldo tersedia");
    try {
      await MerchantAPI.requestWithdrawal(amt);
      toast.success("Permintaan penarikan diajukan — menunggu verifikasi Admin Keuangan");
      setWdOpen(false); setWdAmount(""); loadWd();
    } catch (err) { toast.error(err?.response?.data?.error || "Gagal mengajukan penarikan"); }
  };

  // Ajukan buka gerai
  const [shopForm, setShopForm] = useState({ shop_name: "", shop_type: "GERAI_FISIK_RTRW", bank_name: "", bank_account_number: "" });
  const [regSaving, setRegSaving] = useState(false);
  const submitRegisterShop = async () => {
    if (!shopForm.shop_name || !shopForm.bank_name || !shopForm.bank_account_number) return toast.error("Lengkapi nama toko & rekening");
    setRegSaving(true);
    try {
      await MerchantAPI.registerShop(shopForm);
      toast.success("Pengajuan gerai terkirim — menunggu persetujuan admin");
      loadOrders();
    } catch (err) { toast.error(err?.response?.data?.error || "Gagal mengajukan"); }
    finally { setRegSaving(false); }
  };

  // Pesanan masuk nyata dari backend (toko milik user yang login).
  const [incoming, setIncoming] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [hasShop, setHasShop] = useState(true);

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      setIncoming(await MerchantAPI.orders());
      setHasShop(true);
    } catch (err) {
      // 403 = user belum punya toko/gerai
      if (err?.response?.status === 403) setHasShop(false);
    } finally {
      setLoadingOrders(false);
    }
  };
  useEffect(() => { loadOrders(); loadWd(); }, []);

  // Ambil info toko & render QR statis gerai (untuk transaksi O2O di lokasi fisik).
  const [qrUrl, setQrUrl] = useState(null);
  const [shop, setShop] = useState(null);
  const shopName = shop?.shop_name || user?.business_name;
  const loadShop = () => MerchantAPI.shop().then((s) => {
    setShop(s);
    if (s.qr_code_static) QRCode.toDataURL(s.qr_code_static, { width: 220, margin: 1 }).then(setQrUrl);
  }).catch(() => {});
  useEffect(() => { loadShop(); }, []);

  // Pendapatan 7 hari terakhir & ringkasan penjualan — nyata dari backend.
  const [revenue, setRevenue] = useState({ daily: [], lifetime: 0, total_sales: 0 });
  const loadRevenue = () => MerchantAPI.revenue().then(setRevenue).catch(() => {});
  useEffect(() => { loadRevenue(); }, []);

  // Dialog Edit Toko & Tambah Produk (dipicu dari banner).
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Produk toko (nyata dari backend) — kosong bila belum ada.
  const [products, setProducts] = useState([]);
  const loadProducts = () => MerchantAPI.products()
    .then((rows) => setProducts((rows || []).map((p) => ({
      id: p.id, name: p.product_name, category: p.category, image: p.image_url,
      price: Number(p.price_member), pricePublic: Number(p.price_non_member),
      stock: p.stock_quantity, status: p.status,
    }))))
    .catch(() => {});
  useEffect(() => { loadProducts(); }, []);

  // Prefill nama toko dari "tempat usaha" yang diisi saat pendaftaran.
  useEffect(() => {
    if (user?.business_name) setShopForm((f) => (f.shop_name ? f : { ...f, shop_name: user.business_name }));
  }, [user]);

  // Merchant menerima pesanan (PAID -> PACKED).
  const acceptOrder = async (o) => {
    try {
      await MerchantAPI.accept(o.id);
      toast.success(`Pesanan ${o.order_number} diterima & dikemas`);
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menerima pesanan");
    }
  };

  // Merchant mengirim pesanan (PACKED -> SHIPPED) dengan resi otomatis.
  const shipOrder = async (o) => {
    try {
      const res = await MerchantAPI.ship(o.id, "Kurir Toko", "");
      toast.success(`Pesanan ${o.order_number} dikirim (resi ${res.tracking_number})`);
      loadOrders();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal mengirim pesanan");
    }
  };

  return (
    <div className="space-y-7">
      {/* Storefront banner — data toko nyata dari backend */}
      <Card className="rounded-3xl border-stone-soft overflow-hidden relative">
        <div className="h-36 lg:h-48 relative">
          <img src={shop?.banner_url || sellerStore.banner} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/30 to-transparent" />
        </div>
        <div className="px-6 lg:px-8 pb-7 pt-14 lg:pt-4 relative">
          <img src={shop?.logo_url || sellerStore.logo} alt={shopName || ""} className="absolute -top-12 left-6 lg:left-8 w-24 h-24 lg:w-28 lg:h-28 rounded-2xl border-4 border-white object-cover shadow-xl animate-scale-in" />
          <div className="flex flex-col lg:flex-row gap-5 lg:items-start lg:pl-36">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display font-bold text-2xl lg:text-3xl text-ink" data-testid="seller-shop-name">{shopName || "Toko Saya"}</h2>
                {shop?.shop_status === "APPROVED" && <Badge className="bg-forest text-cream hover:bg-forest rounded-full"><Award size={11} /> Terverifikasi</Badge>}
              </div>
              <p className="text-ink/65 text-sm mt-1">{shop?.description || "Belum ada deskripsi toko. Klik Edit Toko untuk melengkapi."}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm">
                <span className="text-ink/55">{revenue.total_sales} terjual</span>
                {shop?.created_at && <><span className="text-ink/55">·</span><span className="text-ink/55">Bergabung {formatDate(shop.created_at)}</span></>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button onClick={() => setEditOpen(true)} disabled={!shop} variant="outline" className="rounded-full border-stone-soft" data-testid="seller-edit-store"><Edit size={14} /> Edit Toko</Button>
              <Button onClick={() => setAddOpen(true)} disabled={!shop} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="seller-add-product-top">
                <Plus size={14} /> Produk Baru
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <KpiCard icon={Wallet} label="Saldo Penarikan" value={formatRupiah(payout)} sub="Siap ditarik" accent="bg-amber-warm/15 text-amber-700" />
        <KpiCard icon={TrendingUp} label="Pendapatan Total" value={formatRupiah(revenue.lifetime)} sub="Sejak bergabung" accent="bg-forest/10 text-forest" />
        <KpiCard icon={Package} label="Produk Aktif" value={products.filter((p) => p.status === "APPROVED").length} sub={`${products.length} total produk`} accent="bg-sage/20 text-forest" />
        <KpiCard icon={ShoppingBag} label="Pesanan Masuk" value={incoming.length} sub="Perlu diproses" accent="bg-terracotta/15 text-terracotta" pulse />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-cream rounded-full p-1">
          <TabsTrigger value="ringkasan" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white px-5" data-testid="seller-tab-ringkasan">Ringkasan</TabsTrigger>
          <TabsTrigger value="produk" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white px-5" data-testid="seller-tab-produk">Produk Saya</TabsTrigger>
          <TabsTrigger value="pesanan" className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white px-5" data-testid="seller-tab-pesanan">Pesanan Masuk</TabsTrigger>
        </TabsList>

        <TabsContent value="ringkasan" className="pt-6 space-y-6">
          {/* QR statis gerai untuk pembayaran O2O (dipindai anggota di lokasi). */}
          {qrUrl && (
            <Card className="rounded-2xl border-stone-soft p-6 flex items-center gap-6">
              <img src={qrUrl} alt="QR Gerai" className="w-32 h-32 rounded-xl border border-stone-soft" data-testid="seller-qr" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">QR Pembayaran Gerai</div>
                <h3 className="font-display font-bold text-lg text-ink mt-1">{shopName}</h3>
                <p className="text-sm text-ink/60 mt-1 max-w-sm">Tempel QR ini di gerai. Anggota memindainya lewat menu <b>Scan QR Gerai</b> untuk membayar dari saldo dompet.</p>
              </div>
            </Card>
          )}

          <Card className="rounded-2xl border-stone-soft p-6">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="font-display font-semibold text-ink">Pendapatan 7 Hari Terakhir</h3>
                <p className="text-xs text-ink/55">Dalam ribuan rupiah</p>
              </div>
              <Badge className="bg-sage/20 text-forest hover:bg-sage/20 rounded-full">
                <TrendingUp size={11} /> {formatRupiah(revenue.daily.reduce((s, r) => s + r.v * 1000, 0))}
              </Badge>
            </div>
            {revenue.daily.every((r) => r.v === 0) ? (
              <div className="h-60 mt-4 grid place-items-center text-sm text-ink/55">Belum ada pendapatan dalam 7 hari terakhir.</div>
            ) : (
            <div className="h-60 mt-4">
              <ResponsiveContainer>
                <BarChart data={revenue.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
                  <XAxis dataKey="d" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#889285" }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                  <Tooltip cursor={{ fill: "rgba(212,140,69,0.08)" }} />
                  <Bar dataKey="v" radius={[10, 10, 0, 0]}>
                    {revenue.daily.map((_, i) => <Cell key={i} fill={i === revenue.daily.length - 1 ? "#D48C45" : "#2D5A27"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            )}
          </Card>

          <div className="grid lg:grid-cols-2 gap-5">
            <Card className="rounded-2xl border-stone-soft p-6 bg-forest text-cream relative overflow-hidden grain-overlay blob-bg">
              <div className="relative z-10">
                <div className="text-xs uppercase tracking-[0.22em] text-amber-warm font-semibold">Tarik Saldo</div>
                <h3 className="font-display font-bold text-3xl mt-2">{formatRupiah(payout)}</h3>
                <p className="text-cream/80 text-sm mt-2">Dana hasil penjualan yang telah dilepas dari escrow. Cair setelah disetujui Admin Keuangan.</p>
                <Button onClick={() => setWdOpen(true)} disabled={payout <= 0} className="mt-5 rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90 font-semibold disabled:opacity-50" data-testid="seller-withdraw">
                  Tarik Sekarang <ArrowUpRight size={15} />
                </Button>
                {wdList.length > 0 && (
                  <div className="mt-4 text-xs text-cream/80 space-y-1">
                    {wdList.slice(0, 3).map((w) => (
                      <div key={w.id} className="flex justify-between"><span>{formatRupiah(w.amount)}</span><span className="uppercase">{w.status}</span></div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card className="rounded-2xl border-stone-soft p-6">
              <h3 className="font-display font-semibold text-ink">Produk Saya</h3>
              <div className="space-y-3 mt-4">
                {products.length === 0 && <div className="text-sm text-ink/55">Belum ada produk.</div>}
                {products.slice(0, 3).map((p, i) => (
                  <div key={p.id} className="flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${i * 100}ms` }}>
                    <div className="w-7 h-7 rounded-full bg-forest text-white grid place-items-center font-display font-bold text-xs">{i + 1}</div>
                    {p.image ? <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-cream" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-ink truncate">{p.name}</div>
                      <div className="text-xs text-ink/55">Stok {p.stock}</div>
                    </div>
                    <div className="font-display font-bold text-sm text-ink">{formatRupiah(p.price)}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="produk" className="pt-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-display font-semibold text-ink text-lg">Daftar Produk Saya</h3>
            <AddProductDialog onCreated={loadProducts} />
          </div>
          <Card className="rounded-2xl border-stone-soft p-5">
            <Table>
              <TableHeader>
                <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                  <TableHead className="text-[10px] uppercase tracking-wider">Produk</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Kategori</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-right">Harga Anggota</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-center">Stok</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} className="border-stone-soft hover:bg-cream/30" data-testid={`seller-product-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.image ? <img src={p.image} alt={p.name} className="w-11 h-11 rounded-lg object-cover" /> : <div className="w-11 h-11 rounded-lg bg-cream" />}
                        <div className="font-semibold text-sm text-ink line-clamp-1 max-w-[230px]">{p.name}</div>
                      </div>
                    </TableCell>
                    <TableCell><Badge className="bg-stone-soft/40 text-ink/70 hover:bg-stone-soft/40 rounded-full text-[10px]">{p.category}</Badge></TableCell>
                    <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(p.price)}</TableCell>
                    <TableCell className="text-center"><span className="font-display font-bold text-ink">{p.stock}</span></TableCell>
                    <TableCell><Badge className="bg-sage/20 text-forest hover:bg-sage/20 rounded-full text-[10px] uppercase tracking-wider">{p.status === "APPROVED" ? "Aktif" : p.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {products.length === 0 && (
              <div className="py-12 text-center text-sm text-ink/55">Belum ada produk. Klik "Tambah Produk Baru" untuk mulai berjualan.</div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="pesanan" className="pt-6 space-y-6">
          {/* Verifikasi bukti transfer pembeli — penjual yang memverifikasi. */}
          {hasShop && <SellerPaymentVerification onChanged={loadOrders} />}

          {/* Pesanan masuk NYATA dari backend — merchant menerima lalu mengirim. */}
          <Card className="rounded-2xl border-stone-soft p-5">
            {!hasShop ? (
              // Form pengajuan buka gerai/toko (status awal PENDING → disetujui admin).
              <div className="max-w-lg mx-auto py-6 space-y-4">
                <div className="text-center">
                  <Store size={32} className="mx-auto text-forest" />
                  <h3 className="font-display font-bold text-lg text-ink mt-2">Ajukan Buka Gerai / Toko</h3>
                  <p className="text-sm text-ink/60 mt-1">Setelah disetujui admin, Anda bisa berjualan & menerima pesanan.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Toko/Gerai</Label>
                  <Input value={shopForm.shop_name} onChange={(e) => setShopForm({ ...shopForm, shop_name: e.target.value })} className="rounded-lg" data-testid="shop-reg-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-ink/55">Jenis</Label>
                  <select value={shopForm.shop_type} onChange={(e) => setShopForm({ ...shopForm, shop_type: e.target.value })} className="w-full h-10 rounded-lg border border-stone-soft bg-white px-3 text-sm">
                    <option value="GERAI_FISIK_RTRW">Gerai Fisik RT/RW</option>
                    <option value="ONLINE_SUPPLIER">Supplier Online</option>
                    <option value="RESTO_MITRA">Resto Mitra</option>
                    <option value="TOKO_KEMITRAAN">Toko Kemitraan</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-ink/55">Bank</Label>
                    <Input value={shopForm.bank_name} onChange={(e) => setShopForm({ ...shopForm, bank_name: e.target.value })} className="rounded-lg" data-testid="shop-reg-bank" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-ink/55">No. Rekening</Label>
                    <Input value={shopForm.bank_account_number} onChange={(e) => setShopForm({ ...shopForm, bank_account_number: e.target.value })} className="rounded-lg" data-testid="shop-reg-acc" />
                  </div>
                </div>
                <Button onClick={submitRegisterShop} disabled={regSaving} className="w-full rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="shop-reg-submit">
                  {regSaving ? "Mengirim…" : "Ajukan Sekarang"}
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                    <TableHead className="text-[10px] uppercase tracking-wider">No. Pesanan</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Pembeli</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Resi</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-right">Total</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingOrders ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-ink/55 py-10">Memuat…</TableCell></TableRow>
                  ) : incoming.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-ink/55 py-10">Belum ada pesanan masuk.</TableCell></TableRow>
                  ) : incoming.map((o) => {
                    const st = merchantStatus[o.status] || { label: o.status, color: "bg-stone-soft/40 text-ink/70" };
                    return (
                      <TableRow key={o.id} className="border-stone-soft hover:bg-cream/30" data-testid={`seller-order-${o.id}`}>
                        <TableCell className="font-mono text-xs font-semibold text-ink">{o.order_number}</TableCell>
                        <TableCell className="text-sm font-medium">{o.buyer_name}</TableCell>
                        <TableCell className="text-sm text-ink/55">{formatDate(o.created_at)}</TableCell>
                        <TableCell className="text-xs font-mono text-ink/70">{o.tracking_number || "—"}</TableCell>
                        <TableCell><Badge className={`${st.color} rounded-full text-[10px] uppercase tracking-wider`}>{st.label}</Badge></TableCell>
                        <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(o.total)}</TableCell>
                        <TableCell className="text-right">
                          {o.status === "PAID" ? (
                            <Button size="sm" onClick={() => acceptOrder(o)} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid={`seller-accept-${o.id}`}>Terima</Button>
                          ) : o.status === "PACKED" ? (
                            <Button size="sm" onClick={() => shipOrder(o)} className="rounded-full bg-amber-warm text-ink hover:bg-amber-warm/90" data-testid={`seller-ship-${o.id}`}>Kirim</Button>
                          ) : (
                            <span className="text-xs text-ink/45">{o.status === "SHIPPED" ? "Menunggu diterima" : "Selesai"}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Penarikan Dana */}
      <Dialog open={wdOpen} onOpenChange={setWdOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Tarik Dana Penjualan</DialogTitle>
            <DialogDescription>Saldo tersedia: {formatRupiah(payout)}. Dana dikirim ke rekening toko setelah disetujui Admin Keuangan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 mt-2">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Nominal Penarikan</Label>
            <Input type="number" value={wdAmount} onChange={(e) => setWdAmount(e.target.value)} placeholder="cth. 50000" className="rounded-lg" data-testid="withdraw-amount" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setWdOpen(false)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submitWithdraw} data-testid="withdraw-submit">Ajukan Penarikan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Tambah Produk (dipicu tombol "Produk Baru" di banner) */}
      <AddProductDialog open={addOpen} onOpenChange={setAddOpen} hideTrigger onCreated={() => { loadProducts(); loadRevenue(); }} />

      {/* Dialog Edit Toko */}
      <EditShopDialog open={editOpen} onOpenChange={setEditOpen} shop={shop} onSaved={loadShop} />
    </div>
  );
}

// ===== Verifikasi Pembayaran oleh Penjual =====
// Pembeli transfer manual & mengunggah bukti → penjual memeriksa lalu menyetujui/menolak.
// Setuju = pesanan menjadi PAID dan lanjut ke proses Terima → Kirim.
function SellerPaymentVerification({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);

  const load = () => MerchantAPI.paymentConfirmations("PENDING").then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal memproses");
    }
  };

  if (rows.length === 0) return null;

  return (
    <Card className="rounded-2xl border-stone-soft p-5">
      <div className="flex items-center gap-2 mb-1">
        <AlertCircle size={16} className="text-amber-700" />
        <h3 className="font-display font-semibold text-ink">Verifikasi Pembayaran Pembeli</h3>
        <Badge className="bg-amber-warm/20 text-amber-700 border-0 rounded-full">{rows.length} menunggu</Badge>
      </div>
      <p className="text-xs text-ink/55 mb-4">
        Pembeli yang memilih transfer manual mengunggah bukti bayar. Periksa bukti transfer,
        lalu setujui agar pesanan berstatus Dibayar dan bisa Anda proses.
      </p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
              <TableHead className="text-[10px] uppercase tracking-wider">No. Pesanan</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Pembeli</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Jumlah</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Bank Pengirim</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Tanggal</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider">Bukti</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="border-stone-soft" data-testid={`seller-verify-row-${r.id}`}>
                <TableCell className="font-mono text-xs font-semibold text-ink">{r.order_number}</TableCell>
                <TableCell className="text-sm font-medium">{r.sender_name || r.buyer_name}<div className="text-[11px] text-ink/55">{r.buyer_name}</div></TableCell>
                <TableCell className="text-right font-display font-semibold text-ink">{formatRupiah(Number(r.amount))}</TableCell>
                <TableCell className="text-sm text-ink/75">{r.bank_name || "—"}</TableCell>
                <TableCell className="text-sm text-ink/55">{formatDate(r.created_at)}</TableCell>
                <TableCell>
                  {r.proof_url ? (
                    <button onClick={() => setPreview(FILE_BASE + r.proof_url)} className="text-forest hover:underline inline-flex items-center gap-1 text-xs">
                      <Eye size={13} /> Lihat
                    </button>
                  ) : <span className="text-ink/40 text-xs">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" className="rounded-full bg-forest hover:bg-forest-dark text-white h-8"
                      onClick={() => act(() => MerchantAPI.approvePaymentProof(r.id, "Bukti valid"), `Pembayaran ${r.order_number} disetujui — pesanan Dibayar`)}
                      data-testid={`seller-verify-approve-${r.id}`}>
                      Setujui
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta hover:bg-terracotta/10 h-8"
                      onClick={() => act(() => MerchantAPI.rejectPaymentProof(r.id, "Bukti tidak valid / tidak sesuai"), "Bukti ditolak")}>
                      Tolak
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Preview bukti transfer */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-ink/60 grid place-items-center p-6" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl p-4 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-ink text-sm">Bukti Transfer</div>
              <button onClick={() => setPreview(null)} className="text-ink/55 hover:text-ink text-sm">Tutup</button>
            </div>
            <img src={preview} alt="Bukti transfer" className="w-full rounded-lg border border-stone-soft" />
          </div>
        </div>
      )}
    </Card>
  );
}

const EditShopDialog = ({ open, onOpenChange, shop, onSaved }) => {
  const [f, setF] = useState({ shop_name: "", description: "", logo_url: "", banner_url: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (shop) setF({
      shop_name: shop.shop_name || "", description: shop.description || "",
      logo_url: shop.logo_url || "", banner_url: shop.banner_url || "",
    });
  }, [shop, open]);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.shop_name.trim()) return toast.error("Nama toko wajib diisi");
    setSaving(true);
    try {
      await MerchantAPI.updateShop({
        shop_name: f.shop_name.trim(), description: f.description,
        logo_url: f.logo_url, banner_url: f.banner_url,
      });
      toast.success("Data toko diperbarui");
      onOpenChange(false); onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menyimpan toko");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Edit Toko</DialogTitle>
          <DialogDescription>Perbarui nama, deskripsi, logo, dan banner toko Anda.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Toko</Label>
            <Input value={f.shop_name} onChange={upd("shop_name")} className="rounded-lg" data-testid="edit-shop-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Deskripsi</Label>
            <Textarea value={f.description} onChange={upd("description")} placeholder="Ceritakan tentang toko Anda…" className="rounded-lg" data-testid="edit-shop-desc" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">URL Logo (opsional)</Label>
            <Input value={f.logo_url} onChange={upd("logo_url")} placeholder="https://..." className="rounded-lg" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">URL Banner (opsional)</Label>
            <Input value={f.banner_url} onChange={upd("banner_url")} placeholder="https://..." className="rounded-lg" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="edit-shop-submit">
            {saving ? "Menyimpan…" : "Simpan Perubahan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, accent, pulse }) => (
  <Card className="rounded-2xl border-stone-soft p-5 bg-white hover-lift" data-testid={`seller-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between mb-3">
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${accent}`}><Icon size={17} /></div>
      {pulse && <span className="w-2 h-2 rounded-full bg-terracotta animate-pulse"></span>}
    </div>
    <div className="text-[10px] uppercase tracking-wider text-ink/55 font-semibold">{label}</div>
    <div className="font-display font-bold text-xl text-ink mt-1">{value}</div>
    <div className="text-xs text-ink/55 mt-1">{sub}</div>
  </Card>
);

const EMPTY_PRODUCT = { product_name: "", category: "", image_url: "", price_non_member: "", price_member: "", stock_quantity: "" };
const AddProductDialog = ({ onCreated, open: openProp, onOpenChange, hideTrigger }) => {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = controlled ? onOpenChange : setOpenState;
  const [f, setF] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.product_name || f.price_non_member === "" || f.price_member === "") {
      return toast.error("Isi nama produk, harga publik, dan harga anggota");
    }
    setSaving(true);
    try {
      await MerchantAPI.addProduct({
        product_name: f.product_name, category: f.category || "Umum", image_url: f.image_url || undefined,
        price_non_member: Number(f.price_non_member), price_member: Number(f.price_member),
        stock_quantity: Number(f.stock_quantity || 0),
      });
      toast.success("Produk ditambahkan & aktif di katalog");
      setOpen(false); setF(EMPTY_PRODUCT); onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal menambah produk");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="seller-add-product">
            <Plus size={15} /> Tambah Produk Baru
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Tambah Produk Baru</DialogTitle>
          <DialogDescription>Produk langsung aktif di katalog toko koperasi.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Nama Produk</Label>
            <Input value={f.product_name} onChange={upd("product_name")} placeholder="cth. Nasi Box Ayam Bakar" className="rounded-lg" data-testid="add-product-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Kategori</Label>
            <Input value={f.category} onChange={upd("category")} placeholder="Catering / Snack / Sembako..." className="rounded-lg" data-testid="add-product-cat" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Stok Awal</Label>
            <Input type="number" value={f.stock_quantity} onChange={upd("stock_quantity")} placeholder="50" className="rounded-lg" data-testid="add-product-stock" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Harga Dasar Publik (Rp)</Label>
            <Input type="number" value={f.price_non_member} onChange={upd("price_non_member")} placeholder="30000" className="rounded-lg" data-testid="add-product-price" />
            {Number(f.price_non_member) > 0 && (
              <p className="text-[11px] text-ink/55">Tampil di etalase: <b>{formatRupiah(Math.round(Number(f.price_non_member)) + Math.round(Number(f.price_non_member) * 0.05))}</b> (+ fee layanan 5%)</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">Harga Dasar Anggota (Rp)</Label>
            <Input type="number" value={f.price_member} onChange={upd("price_member")} placeholder="27000" className="rounded-lg" data-testid="add-product-member-price" />
            {Number(f.price_member) > 0 && (
              <p className="text-[11px] text-ink/55">Tampil di etalase: <b>{formatRupiah(Math.round(Number(f.price_member)) + Math.round(Number(f.price_member) * 0.05))}</b> (+ fee layanan 5%)</p>
            )}
          </div>
          <div className="sm:col-span-2 rounded-xl bg-cream p-3 text-[11px] text-ink/70">
            Harga dasar adalah hak Anda <b>100%</b> — dibayarkan penuh saat pesanan selesai. Koperasi menambahkan
            fee layanan 5% di atasnya pada harga etalase; fee inilah yang dibagi untuk SHU & operasional koperasi.
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-ink/55">URL Foto Produk (opsional)</Label>
            <Input value={f.image_url} onChange={upd("image_url")} placeholder="https://..." className="rounded-lg" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-forest hover:bg-forest-dark text-white" data-testid="add-product-submit">
            {saving ? "Menyimpan…" : "Simpan Produk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
